import React, { useState, useEffect, useRef } from "react";
import {
  IonText,
  IonIcon,
  IonPage,
  useIonRouter,
  IonContent,
  useIonViewWillEnter,
} from "@ionic/react";
import { TherapyItem } from "../../components/therapy/BiWazeTherapyCard";
import { useStorage } from "../../contexts/StorageContext";
import { chevronForward } from "ionicons/icons";
import Header from "../../components/Header";
import BiWazeGoalModal, {
  BiWazeGoalModalRef,
} from "../../components/ui/popups/BiwazeGoals";
import { fetchGoals, createGoal, updateGoal } from "../../services/goals";
import useAuth from "../../hooks/useAuth";
import { getIonicStorage, ionicStorage } from "../../services/storageService";
import { getUserDevices } from "../../services/device";
import ActionButtons from "../../components/ui/popups/ActionButtons";
import { clearGoalLabel } from "../../utils/goalLabel";

// Maps a BiWaze goal type to the device type string required
const BIWAZE_GOAL_DEVICE_MAP: Record<string, string> = {
  biwaze_cough: "biwaze_cough_system",
  biwaze_clear: "biwaze_clear_system",
};

const MyGoals: React.FC = () => {
  const modalRef = useRef<BiWazeGoalModalRef>(null);
  const [selectedGoal, setSelectedGoal] = useState<any>(null);
  const [connectedDeviceGoals, setConnectedDeviceGoals] = useState<any[]>([]);
  const [treatmentGoals, setTreatmentGoals] = useState<any[]>([]);
  const [healthGoals, setHealthGoals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true); // Loader state
  const router = useIonRouter();
  const [fromParam, setFromParam] = useState<string | null>(null);
  const [goalIdParam, setGoalIdParam] = useState<string | null>(null);
  const [goalTypeParam, setGoalTypeParam] = useState<string | null>(null);

  // useIonViewWillEnter fires every time the view is entered (including Ionic cached instances),
  // so query params are always re-read correctly on repeated navigation.
  useIonViewWillEnter(() => {
    const params = new URLSearchParams(window.location.search);
    setFromParam(params.get("from"));
    setGoalIdParam(params.get("goalId"));
  });
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [therapyData, setTherapyData] = useState<TherapyItem[]>([]); // Therapy data state
  const [allGoals, setAllGoals] = useState<any[]>([]);
  const [linkedDevices, setLinkedDevices] = useState<any[]>([]);
  const [connectDevicePrompt, setConnectDevicePrompt] = useState<{
    show: boolean;
    goalType: string | null;
  }>({ show: false, goalType: null });
  const { storage, isStorageReady } = useStorage();
  const { user } = useAuth(); // Assuming you have a useAuth hook to get user data
  // Label mapping for goal types
  const goalLabels: Record<string, string> = {
    biwaze_cough: "BiWaze® Cough",
    biwaze_clear: "BiWaze® Clear",
    vest_treatment: "Vest Treatments",
    breathing_treatment: "Breathing Routines",
    step_tracking: "Steps",
    sleep_tracking: "Sleep",
  };
  // Returns true when the goal doesn't need a device, or the required device is linked
  const hasLinkedDevice = (goalType: string): boolean => {
    const requiredDeviceType = BIWAZE_GOAL_DEVICE_MAP[goalType];
    if (!requiredDeviceType) return true; // non-BiWaze goals are always allowed
    return linkedDevices.some((d: any) => d.type === requiredDeviceType);
  };

  // All BiWaze goals are always shown so the user can see what's available;
  // clicking one without a linked device triggers the connect-device prompt.

  const loadDevices = async () => {
    // Show cached devices immediately
    if (storage && isStorageReady) {
      const stored = await getIonicStorage(storage, "myDevices");
      if (stored && Array.isArray(stored)) setLinkedDevices(stored);
    }
    // Refresh from backend
    try {
      if (user?.profile_id) {
        const fresh = await getUserDevices(user.profile_id);
        setLinkedDevices(fresh);
        if (storage && isStorageReady) {
          await ionicStorage(storage, "myDevices", fresh);
        }
      }
    } catch (err) {
      console.error("Error fetching devices:", err);
    }
  };

  useEffect(() => {
    if (goalIdParam)
      setTimeout(() => {
        setGoalTypeParam(decodeURIComponent(goalIdParam ?? "").toLowerCase());
      }, 500);
  }, [goalIdParam]);

  // Runs when either goalTypeParam or allGoals changes so the lookup succeeds
  // even if goals finish loading after the param is already set.
  // goalTypeParam is cleared immediately after the goal is found to prevent
  // subsequent allGoals updates (cache → API) from re-opening the modal.
  useEffect(() => {
    if (!goalTypeParam || allGoals.length === 0) return;
    const goalType = Object.entries(goalLabels).find(
      ([, label]) => label.toLowerCase() === goalTypeParam
    )?.[0];
    if (goalType) {
      const goalToEdit = allGoals.find((goal) => goal.goal_type === goalType);
      if (goalToEdit) {
        setGoalTypeParam(null); // prevent re-trigger on future allGoals updates
        if (!hasLinkedDevice(goalToEdit.goal_type)) {
          setConnectDevicePrompt({ show: true, goalType: goalToEdit.goal_type });
          return;
        }
        setSelectedGoal(goalToEdit);
      }
    }
  }, [goalTypeParam, allGoals, linkedDevices]);

  // Auto-present modal when selectedGoal changes
  useEffect(() => {
    if (selectedGoal && modalRef.current) {
      modalRef.current.present();
    }
  }, [selectedGoal]);

  // Default structure for missing goals
  const defaultGoals: Record<string, any> = {
    biwaze_cough: {
      goal_type: "biwaze_cough",
      target_count: null,
      target_seconds: null,
      target_hours: null,
      measurement_unit: "count",
      days_of_week: [],
    },
    biwaze_clear: {
      goal_type: "biwaze_clear",
      target_count: null,
      target_seconds: null,
      target_hours: null,
      measurement_unit: ["count", "minutes"],
      days_of_week: [],
    },
    vest_treatment: {
      goal_type: "vest_treatment",
      target_count: null,
      target_seconds: null,
      target_hours: null,
      measurement_unit: "count",
      days_of_week: [],
    },
    breathing_treatment: {
      goal_type: "breathing_treatment",
      target_count: null,
      target_seconds: null,
      target_hours: null,
      measurement_unit: "count",
      days_of_week: [],
    },
    step_tracking: {
      goal_type: "step_tracking",
      target_count: null,
      target_seconds: null,
      target_hours: null,
      target_steps: null,
      measurement_unit: "minutes",
      days_of_week: [],
    },
    sleep_tracking: {
      goal_type: "sleep_tracking",
      target_count: null,
      target_seconds: null,
      target_hours: null,
      measurement_unit: "hours",
      days_of_week: [],
    },
  };

  useEffect(() => {
    loadGoals();
    initGoals();
  }, []);

  // Keeping it separate from the goals effect avoids a second /devices call on mount.
  useEffect(() => {
    if (isStorageReady && user?.profile_id) {
      loadDevices();
    }
  }, [isStorageReady, user?.profile_id]);
  const initGoals = async () => {
    let StorageGoals;
    try {
      // const storedGoals = await Preferences.get({ key: "goals" });
      if (storage && isStorageReady) {
        StorageGoals = await getIonicStorage(storage, "goals");
      }
      if (StorageGoals.value) {
        const parsedGoals = JSON.parse(StorageGoals.value);

        // Categorize the goals
        const connectedDevices = parsedGoals.filter((goal: any) =>
          ["biwaze_cough", "biwaze_clear"].includes(goal.goal_type),
        );
        const treatments = parsedGoals.filter((goal: any) =>
          ["vest_treatment", "breathing_treatment"].includes(goal.goal_type),
        );
        const health = parsedGoals.filter((goal: any) =>
          ["step_tracking", "sleep_tracking"].includes(goal.goal_type),
        );

        setConnectedDeviceGoals(connectedDevices);
        setTreatmentGoals(treatments);
        setHealthGoals(health);
      }
    } catch (error) {
      console.error("Error loading goals from Ionic Storage:", error);
    }
  };
  const loadGoals = async () => {
    try {
      // setLoading(true);

      if (storage && isStorageReady) {
        const storedGoals = await getIonicStorage(storage, "goals");
        console.log("Stored Goals:", storedGoals);
        if (storedGoals) {
          const parsedGoals = storedGoals;
          setAllGoals(parsedGoals);
          // Categorize the goals
          const connectedDevices = parsedGoals.filter((goal: any) =>
            ["biwaze_cough", "biwaze_clear"].includes(goal.goal_type),
          );
          const treatments = parsedGoals.filter((goal: any) =>
            ["vest_treatment", "breathing_treatment"].includes(goal.goal_type),
          );
          const health = parsedGoals.filter((goal: any) =>
            ["step_tracking", "sleep_tracking"].includes(goal.goal_type),
          );
          setConnectedDeviceGoals(connectedDevices);
          setTreatmentGoals(treatments);
          setHealthGoals(health);
          setLoading(false);
        }
      }
      const response = await fetchGoals({
        profile_id: user?.profile_id,
        isActive: true,
      });
      const goals = response.goals;
      console.log("Fetched Goals:", goals);
      // Merge fetched goals with default goals
      const mergedGoals = Object.values(defaultGoals).map((defaultGoal) => {
        const existingGoal = goals.find(
          (goal: any) => goal.goal_type === defaultGoal.goal_type,
        );
        return existingGoal || defaultGoal;
      });
      // Save merged goals to Preferences
      // await Preferences.set({
      //   key: "goals",
      //   value: JSON.stringify(mergedGoals),
      // });
      if (storage && isStorageReady) {
        await ionicStorage(storage, "goals", mergedGoals);
      }
      // Update state with merged goals
      setAllGoals(mergedGoals);

      // Categorize goals
      const connectedDevices = mergedGoals.filter((goal: any) =>
        ["biwaze_cough", "biwaze_clear"].includes(goal.goal_type),
      );
      const treatments = mergedGoals.filter((goal: any) =>
        ["vest_treatment", "breathing_treatment"].includes(goal.goal_type),
      );
      const health = mergedGoals.filter((goal: any) =>
        ["step_tracking", "sleep_tracking"].includes(goal.goal_type),
      );

      setConnectedDeviceGoals(connectedDevices);
      setTreatmentGoals(treatments);
      setHealthGoals(health);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error loading goals:", error);
    }
  };

  const handleSave = async (goalData: {
    profile_id: number;
    goal_type: string;
    target_count?: number;
    target_seconds?: number;
    target_hours?: number;
    measurement_unit?: string;
    days_of_week?: number[];
    isActive?: boolean;
    effective_from?: string;
    id?: number;
  }) => {
    // Safety guard: block BiWaze goals if no device is linked
    if (!hasLinkedDevice(goalData.goal_type)) {
      setConnectDevicePrompt({ show: true, goalType: goalData.goal_type });
      return;
    }
    try {
      // Create a clean copy of goalData
      const cleanedGoalData: any = { ...goalData };
      cleanedGoalData.profile_id = user?.profile_id;
      // Remove target fields based on goal_type
      switch (cleanedGoalData.goal_type) {
        case "biwaze_cough":
          cleanedGoalData.target_seconds = undefined;
          cleanedGoalData.target_hours = undefined;
          cleanedGoalData.measurement_unit = "count";
          break;
        case "biwaze_clear":
          cleanedGoalData.target_hours = undefined;
          cleanedGoalData.measurement_unit = "count";
          break;
        case "vest_treatment":
        case "breathing_treatment":
          cleanedGoalData.target_seconds = undefined;
          cleanedGoalData.target_hours = undefined;
          cleanedGoalData.measurement_unit = "count";
          break;
        case "step_tracking":
          cleanedGoalData.target_seconds = undefined;
          cleanedGoalData.target_hours = undefined;
          cleanedGoalData.measurement_unit = "count";
          break;
        case "sleep_tracking":
          cleanedGoalData.target_count = undefined;
          cleanedGoalData.target_seconds = undefined;
          cleanedGoalData.measurement_unit = "hours";
          break;
      }

      // Call the createGoal function to create a new goal
      await createGoal(cleanedGoalData);
      await loadGoals();
    } catch (error) {
      console.error("Error saving goal:", error);
    }
  };

  const handleGoalClick = (goal: any) => {
    if (!hasLinkedDevice(goal.goal_type)) {
      setConnectDevicePrompt({ show: true, goalType: goal.goal_type });
      return;
    }
    setSelectedGoal(goal);
  };

  const handleChange = (goal_type: string, goalObject: any) => {
    const updatedGoalObject = {
      ...goalObject,
      isActive: Object.prototype.hasOwnProperty.call(goalObject, "isActive")
        ? goalObject.isActive
        : true,
    };
    const updatedGoals = allGoals.map((goal: any) =>
      goal.goal_type === goal_type ? updatedGoalObject : goal,
    );
    setAllGoals(updatedGoals);
    setSelectedGoal(updatedGoalObject);
  };
  const handleRemoveGoal = async (goalData: any) => {
    try {
      if (goalData.id) {
        await updateGoal(goalData.id, {
          isActive: false,
          effective_to: new Date().toLocaleDateString("en-CA"),
        });
        loadGoals();
      }
    } catch (error) {
      console.error("Error removing goal:", error);
    }
  };
  const renderGoals = (goals: any[]) => {
    return goals.map((goal: any, index: number) => (
      <div
        key={goal.goal_type}
        className={`p-4 relative ${
          index !== goals.length - 1 ? "border-b border-gray-200" : ""
        }`}
      >
        <div className="cursor-pointer" onClick={() => handleGoalClick(goal)}>
          <h3 className="font-figtree font-medium text-[18px] leading-[22px] h-[22px] text-black mb-1 font-feature-case">
            {goalLabels[goal.goal_type] || "Unknown Goal"}
          </h3>
          <p className="text-gray-500">
            {!hasLinkedDevice(goal.goal_type)
              ? "No Linked Device"
              : !goal.isActive
              ? "Tap to set"
              : goal.goal_type === "biwaze_clear"
              ? clearGoalLabel(goal) || "Tap to set"
              : goal.goal_type === "step_tracking" &&
                goal?.target_count !== undefined &&
                goal?.target_count !== null
              ? `${goal.target_count} steps/day`
              : goal.measurement_unit === "count" &&
                goal?.target_count !== undefined &&
                goal?.target_count !== null
              ? `${goal.target_count}x/day`
              : goal.measurement_unit === "minutes" &&
                goal?.target_seconds !== undefined &&
                goal?.target_seconds !== null
              ? `${Math.round(goal.target_seconds / 60)} min/day`
              : goal.measurement_unit === "hours" &&
                goal?.target_hours !== undefined &&
                goal?.target_hours !== null
              ? `${goal.target_hours} hrs/day`
              : "Tap to set"}
          </p>
        </div>

        <button
          className="absolute top-0 right-4 bottom-0 flex items-center justify-center h-full"
          onClick={() => handleGoalClick(goal)}
        >
          <IonIcon icon={chevronForward} style={{ fontSize: "24px" }} />
        </button>
      </div>
    ));
  };
  const handleBack = () => {
    if (fromParam) {
      router.push(`/${fromParam}`, "back");
      return;
    }
    router.push("/settings", "back");
  };

  return (
    <IonPage>
      <Header
        showGreeting={false}
        showPageTitle={true}
        showUserIcon={false}
        pageTitle="My Goals"
        showBackButton={true}
        onBackButtonClick={handleBack}
      />
      <IonContent
        className="ion-content-page !m-2 !p-4"
        style={{ backgroundColor: "#FFF !important" }}
      >
        <div className="flex flex-col m-4 " style={{ paddingBottom: "80px" }}>
          {/* Connected Devices — always shown; tapping a goal without a linked device shows the connect prompt */}
          {connectedDeviceGoals.length > 0 && (
            <>
              <div
                className="flex flex-col h-full"
                style={{ paddingBottom: "14px" }}
              >
                <div className="section-header ms-4  ">
                  <IonText>
                    <h2
                      className="text-gray-500 font-medium "
                      style={{ fontSize: "12px" }}
                    >
                      LINKED DEVICES
                    </h2>
                  </IonText>
                </div>
              </div>

              <div className="bg-white rounded-xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.1)] mb-6">
                {renderGoals(connectedDeviceGoals)}
              </div>
            </>
          )}

          {/* Other Treatment Goals */}
          <div className="section-header ms-4 mb-4">
            <IonText>
              <h2
                className="text-gray-500 font-medium "
                style={{ fontSize: "12px" }}
              >
                OTHER TREATMENT GOALS
              </h2>
            </IonText>
          </div>
          <div className="bg-white rounded-xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.1)] mb-6">
            {renderGoals(treatmentGoals)}
          </div>

          {/* Health Goals */}
          <div className="section-header ms-4 mb-4">
            <IonText>
              <h2
                className="text-gray-500 font-medium "
                style={{ fontSize: "12px" }}
              >
                HEALTH GOALS
              </h2>
            </IonText>
          </div>
          <div className="bg-white rounded-xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.1)] mb-6">
            {renderGoals(healthGoals)}
          </div>
        </div>

        {selectedGoal && (
          <BiWazeGoalModal
            ref={modalRef}
            onClose={() => setSelectedGoal(null)}
            goalData={selectedGoal}
            onSave={async (goalData) => {
              await handleSave(goalData);
            }}
            onRemoveGoal={handleRemoveGoal}
            onChange={(goalData) => {
              setSelectedGoal(goalData);
              handleChange(goalData.goal_type, goalData);
            }}
            noOfTimes={
              selectedGoal.goal_type === "sleep_tracking"
                ? "Hours per night"
                : ""
            }
            deviceName={goalLabels[selectedGoal.goal_type] || "Unknown Goal"}
            showNumbers={
              selectedGoal.goal_type === "biwaze_cough" ||
              selectedGoal.goal_type === "biwaze_clear" ||
              selectedGoal.goal_type === "vest_treatment" ||
              selectedGoal.goal_type === "breathing_treatment"
            }
            showMinutes={selectedGoal.goal_type === "biwaze_clear"}
            showSteps={selectedGoal.goal_type === "step_tracking"}
            showSleepGoal={selectedGoal.goal_type === "sleep_tracking"}
          />
        )}

        {/* Connect device prompt — shown when user taps a BiWaze goal with no linked device */}
       {connectDevicePrompt.show && <ActionButtons
          isOpen={connectDevicePrompt.show}
          onClose={() => setConnectDevicePrompt({ show: false, goalType: null })}
          title={goalLabels[connectDevicePrompt.goalType ?? ""] ?? "BiWaze®"}
          message={`Connect a ${goalLabels[connectDevicePrompt.goalType ?? ""] ?? "BiWaze®"} device to set goals for this therapy.`}
          actionButtons={[
            {
              text: "Connect device",
              isPrimary: true,
              action: () => {
                setConnectDevicePrompt({ show: false, goalType: null });
                router.push("/settings/devices?linkDevice=true", "forward");
              },
            },
          ]}
        />}
      </IonContent>
    </IonPage>
  );
};

export default MyGoals;
